import axios from 'axios';

// Smart backend URL detection with runtime configuration support
const getBackendURL = () => {
  // 1. Check for runtime configuration (allows post-build changes)
  if (typeof window !== 'undefined' && window.APP_CONFIG?.BACKEND_URL) {
    return window.APP_CONFIG.BACKEND_URL;
  }

  // 2. Check for explicit environment variable (build-time config)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // 3. In production (Vercel), warn if backend URL is not configured
  if (process.env.NODE_ENV === 'production') {
    console.warn('[API] Backend URL not configured. Set REACT_APP_BACKEND_URL or edit build/config.js');
    console.warn('[API] Backend features will be unavailable until configured.');
    return null;
  }

  // 4. Development default
  return 'http://localhost:8000';
};

const BACKEND_URL = getBackendURL();
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : null;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const HEALTH_CHECK_INTERVAL = 30000; // 30 seconds

class APIService {
  constructor() {
    this.baseURL = API_BASE;
    this.healthStatus = API_BASE ? 'unknown' : 'unavailable';
    this.retryCount = 0;
    this.healthCheckInterval = null;

    // If no backend URL is configured, create a mock client
    if (!API_BASE) {
      console.warn('[API] Backend not configured. API calls will fail gracefully.');
      this.client = null;
      return;
    }

    // Create axios instance with interceptors
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        console.log(`[API] ${config.method.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor with retry logic
    this.client.interceptors.response.use(
      (response) => {
        this.retryCount = 0; // Reset on success
        return response;
      },
      async (error) => {
        const config = error.config;

        // Don't retry if we've already retried MAX_RETRIES times
        if (!config || !config.retry) {
          config.retry = 0;
        }

        if (config.retry >= MAX_RETRIES) {
          console.error(`[API] Max retries (${MAX_RETRIES}) exceeded for ${config.url}`);
          return Promise.reject(error);
        }

        // Retry on network errors or 5xx server errors
        if (
          error.code === 'ECONNABORTED' ||
          error.message === 'Network Error' ||
          !error.response ||
          (error.response.status >= 500 && error.response.status <= 599)
        ) {
          config.retry += 1;
          const delayTime = RETRY_DELAY * Math.pow(2, config.retry - 1); // Exponential backoff

          console.log(
            `[API] Retrying request (${config.retry}/${MAX_RETRIES}) to ${config.url} after ${delayTime}ms`
          );

          await this.delay(delayTime);
          return this.client(config);
        }

        return Promise.reject(error);
      }
    );

    // Start health monitoring
    this.startHealthMonitoring();
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Check if backend is configured
  isConfigured() {
    return this.client !== null;
  }

  async checkHealth() {
    if (!this.isConfigured()) {
      return { status: 'unavailable', error: 'Backend not configured' };
    }

    try {
      const response = await axios.get(`${this.baseURL}/health`, { timeout: 5000 });
      this.healthStatus = response.data.status || 'healthy';
      return response.data;
    } catch (error) {
      console.error('[API] Health check failed:', error.message);
      this.healthStatus = 'unhealthy';
      return { status: 'unhealthy', error: error.message };
    }
  }

  startHealthMonitoring() {
    // Initial health check
    this.checkHealth();

    // Periodic health checks
    this.healthCheckInterval = setInterval(() => {
      this.checkHealth();
    }, HEALTH_CHECK_INTERVAL);
  }

  stopHealthMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  getHealthStatus() {
    return this.healthStatus;
  }

  // Helper to check if API is available
  _checkAvailable() {
    if (!this.isConfigured()) {
      throw new Error('Backend API is not configured. Please set REACT_APP_BACKEND_URL environment variable.');
    }
  }

  // Project APIs
  async getProjects() {
    this._checkAvailable();
    const response = await this.client.get('/projects');
    return response.data;
  }

  async getProject(projectId) {
    this._checkAvailable();
    const response = await this.client.get(`/projects/${projectId}`);
    return response.data;
  }

  async createProject(projectData) {
    this._checkAvailable();
    const response = await this.client.post('/projects', projectData);
    return response.data;
  }

  async updateProject(projectId, projectData) {
    const response = await this.client.put(`/projects/${projectId}`, projectData);
    return response.data;
  }

  async deleteProject(projectId) {
    const response = await this.client.delete(`/projects/${projectId}`);
    return response.data;
  }

  async importGitHubProject(importData) {
    const response = await this.client.post('/projects/import-github', importData);
    return response.data;
  }

  // File APIs
  async getFiles(projectId) {
    const response = await this.client.get(`/projects/${projectId}/files`);
    return response.data;
  }

  async getFile(fileId) {
    const response = await this.client.get(`/files/${fileId}`);
    return response.data;
  }

  async createFile(projectId, fileData) {
    const response = await this.client.post(`/projects/${projectId}/files`, fileData);
    return response.data;
  }

  async uploadFile(projectId, file, path = '') {
    const formData = new FormData();
    formData.append('file', file);
    if (path) formData.append('path', path);

    const response = await this.client.post(`/projects/${projectId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateFile(fileId, fileData) {
    const response = await this.client.put(`/files/${fileId}`, fileData);
    return response.data;
  }

  async deleteFile(fileId) {
    const response = await this.client.delete(`/files/${fileId}`);
    return response.data;
  }

  // Code Analysis APIs
  async analyzeCode(analysisData) {
    const response = await this.client.post('/analyze', analysisData);
    return response.data;
  }

  async analyzeCodeSimple(analysisData) {
    const response = await this.client.post('/analyze-simple', analysisData);
    return response.data;
  }

  // AI APIs
  async generateWithAI(aiRequest) {
    const response = await this.client.post('/ai/generate', aiRequest);
    return response.data;
  }

  // Build and Debug APIs
  async executeBuild(buildRequest) {
    const response = await this.client.post('/build/execute', buildRequest);
    return response.data;
  }

  async diagnoseError(diagnosticRequest) {
    const response = await this.client.post('/debug/diagnose', diagnosticRequest);
    return response.data;
  }

  async autoFixFile(autoFixRequest) {
    const response = await this.client.post('/debug/auto-fix', autoFixRequest);
    return response.data;
  }

  // GitHub Sync APIs
  async getModifiedFiles(projectId) {
    const response = await this.client.get(`/projects/${projectId}/modified-files`);
    return response.data;
  }

  async getFileDiff(fileId) {
    const response = await this.client.get(`/files/${fileId}/diff`);
    return response.data;
  }

  async syncToGitHub(projectId, syncRequest) {
    const response = await this.client.post(`/projects/${projectId}/sync-to-github`, syncRequest);
    return response.data;
  }
}

// Export singleton instance
const apiService = new APIService();
export default apiService;
